import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import TomSelect from '../../components/common/TomSelect';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { useSearch } from '../../hooks/useSearch';
import { useSelection } from '../../hooks/useSelection';
import Pagination from '../../components/common/Pagination';
import { usePagination } from '../../hooks/usePagination';
import { useAuth } from '../../contexts/AuthProvider';

const Workflow = () => {
  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canWorkflow = (key) => can(key, 'workflow');

  // Pagination
  const pagination = usePagination(10);

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const { search, handleSearchChange } = useSearch((value) => {
    pagination.setPage(0);
    fetchWorkflows(value);
  });

  // Selection
  const {
    selectedIds,
    setSelectedIds,
    handleCheckboxClick,
    handleSelectAll
  } = useSelection();

  // Data and form state
  const [workflows, setWorkflows] = useState([]);
  const [formData, setFormData] = useState({
    workflowName: ''
  });
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState([
    { step: 1, jobTitleId: '', qualificationId: '', revisionTargetStep: '', rejectTargetStep: '' }
  ]);
  const [enableRevision, setEnableRevision] = useState(true);
  const [enableReject, setEnableReject] = useState(false);
  const [jobTitleOptions, setJobTitleOptions] = useState([]);
  const [competencyOptions, setCompetencyOptions] = useState([]);
  const [jobTitlesMap, setJobTitlesMap] = useState({});
  const [qualificationsMap, setQualificationsMap] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');
  const [showForm, setShowForm] = useState(false);

  const resetFormData = useCallback(() => {
    setFormData({
      workflowName: ''
    });
    setDescription('');
    setSteps([{ step: 1, jobTitleId: '', qualificationId: '', revisionTargetStep: '', rejectTargetStep: '' }]);
    setEnableRevision(true);
    setEnableReject(false);
    setErrors({});
    setEditingId(null);
    setShowForm(false);
  }, []);

  // Fetch workflows
  const fetchWorkflows = async (searchValue = search) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/audit/workflows`, {
        params: {
          search: searchValue,
          page: pagination.page,
          size: pagination.size,
        }
      });

      const responseData = response?.data;
      const items = Array.isArray(responseData)
        ? responseData
        : responseData?.content || responseData?.items || responseData?.data || [];
      const total = responseData?.totalElements ?? responseData?.total ?? items.length;
      const totalPages = responseData?.totalPages ?? (items.length > 0 ? Math.ceil(total / pagination.size) : 0);

      setWorkflows(items);
      pagination.setTotal(total);
      pagination.setTotalPages(totalPages);
      pagination.setCurrentPageItems(items.length);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      setFailMessage(error?.response?.data?.message || 'Failed to load workflows');
      setShowFailModal(true);
      setWorkflows([]);
      pagination.setTotal(0);
      pagination.setTotalPages(0);
      pagination.setCurrentPageItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSelectOptions();
  }, []);

  useEffect(() => {
    fetchWorkflows();
  }, [pagination.page, pagination.size, search]);

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleStepChange = (index, field, value) => {
    setSteps((prev) => {
      const next = prev.map((step, i) => (i === index ? { ...step, [field]: value } : step));
      return next;
    });

    if (errors.steps?.[index]?.[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        const stepErrors = Array.isArray(next.steps) ? [...next.steps] : [];
        if (stepErrors[index]) {
          stepErrors[index] = { ...stepErrors[index], [field]: '' };
        }
        next.steps = stepErrors;
        return next;
      });
    }
  };

  const addStep = () => {
    setSteps((prev) => {
      const nextStepNumber = prev.length + 1;
      return [
        ...prev,
        { step: nextStepNumber, jobTitleId: '', qualificationId: '', revisionTargetStep: '1', rejectTargetStep: '' }
      ];
    });
  };

  const removeStep = (index) => {
    setSteps((prev) => {
      const removedStepNumber = prev[index]?.step;
      const filtered = prev.filter((_, i) => i !== index);

      return filtered.map((step, i) => {
        const nextStepNumber = i + 1;
        const remapTargetStep = (targetStep) => {
          if (targetStep === '' || targetStep === null || targetStep === undefined) return '';
          if (targetStep === 0 || targetStep === '0') return '0';

          const numericTarget = Number(targetStep);
          if (!Number.isFinite(numericTarget)) return '';

          const adjustedTarget = removedStepNumber && numericTarget > removedStepNumber
            ? numericTarget - 1
            : numericTarget;

          if (adjustedTarget <= 0 || adjustedTarget >= nextStepNumber) {
            return '';
          }

          return String(adjustedTarget);
        };

        return {
          ...step,
          step: nextStepNumber,
          revisionTargetStep: nextStepNumber === 1 ? '' : (remapTargetStep(step.revisionTargetStep) || '1'),
          rejectTargetStep: nextStepNumber === 1 ? '' : remapTargetStep(step.rejectTargetStep)
        };
      });
    });
  };

  const getRevisionTargetOptions = (stepIndex) => {
    return steps
      .filter((_, i) => i < stepIndex)
      .map((step) => ({
        value: String(step.step),
        label: `Back to Assignee (Step ${step.step})`
      }));
  };

  const getRejectTargetOptions = (stepIndex) => {
    if (stepIndex === 0) return [];
    const stepOptions = steps
      .filter((_, i) => i < stepIndex)
      .map((step) => ({
        value: String(step.step),
        label: `Back to Assignee (Step ${step.step})`
      }));
    return [
      ...stepOptions,
      { value: '0', label: 'Finish (Application locked, cannot be edited)' }
    ];
  };

  const getRejectTargetLabel = (rejectTargetStep) => {
    if (!rejectTargetStep && rejectTargetStep !== 0) return '-';
    if (rejectTargetStep === 0 || rejectTargetStep === '0') return 'Finish (Locked)';
    return `Step ${rejectTargetStep}`;
  };

  const getJobTitleName = (jobTitleId) => {
    if (!jobTitleId) return '-';
    return jobTitlesMap[jobTitleId] || `ID: ${jobTitleId}`;
  };

  const getQualificationName = (qualificationId) => {
    if (!qualificationId) return '-';
    return qualificationsMap[qualificationId] || `ID: ${qualificationId}`;
  };

  const fetchSelectOptions = async () => {
    try {
      const [jobTitleRes, qualificationRes] = await Promise.all([
        api.get('/md/job-title').catch(() => ({ data: [] })),
        api.get('/audit/personnel-qualifications').catch(() => ({ data: [] }))
      ]);

      const jobTitles = Array.isArray(jobTitleRes?.data)
        ? jobTitleRes.data
        : jobTitleRes?.data?.content || jobTitleRes?.data?.items || jobTitleRes?.data?.data || [];
      const jobTitleOpts = jobTitles
        .map((item) => ({
          value: String(item?.id || ''),
          label: String(item?.name || '').trim()
        }))
        .filter((opt) => opt.value && opt.label);

      // Create mapping from ID to name for job titles
      const jobTitlesMapping = {};
      jobTitles.forEach((item) => {
        if (item?.id && item?.name) {
          jobTitlesMapping[item.id] = item.name;
        }
      });
      setJobTitlesMap(jobTitlesMapping);

      // Qualification options: value=id, label=qualification
      const qualifications = Array.isArray(qualificationRes?.data)
        ? qualificationRes.data
        : qualificationRes?.data?.content || qualificationRes?.data?.items || qualificationRes?.data?.data || [];
      const qualificationOpts = qualifications
        .map((item) => ({
          value: String(item.id),
          label: String(item.qualification)
        }))
        .filter((opt) => opt.value && opt.label);

      // Mapping id ke nama
      const qualificationsMapping = {};
      qualifications.forEach((item) => {
        if (item?.id && item?.qualification) {
          qualificationsMapping[item.id] = item.qualification;
        }
      });
      setQualificationsMap(qualificationsMapping);

      setJobTitleOptions(jobTitleOpts);
      setCompetencyOptions(qualificationOpts);
    } catch (error) {
      console.error('Failed to load options:', error);
      setJobTitleOptions([]);
      setCompetencyOptions([]);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.workflowName?.trim()) {
      newErrors.workflowName = 'Workflow name is required';
    }
    // Description is now optional - no validation needed

    const stepErrors = steps.map((step, index) => {
      const stepError = {};
      // ✅ Allow either Job Title OR Qualification (not both required, at least one required)
      const hasJobTitleId = step.jobTitleId && String(step.jobTitleId).trim();
      const hasQualificationId = step.qualificationId && String(step.qualificationId).trim();
      if (!hasJobTitleId && !hasQualificationId) {
        stepError.general = 'Please select either Job Title or Qualification';
      }
      if (enableRevision && index > 0 && !String(step.revisionTargetStep || '').trim()) {
        stepError.revisionTargetStep = 'Select revision target';
      }
      if (enableReject && index > 0 && step.rejectTargetStep === '') {
        stepError.rejectTargetStep = 'Select reject target';
      }
      return stepError;
    });

    if (stepErrors.some((err) => Object.keys(err).length > 0)) {
      newErrors.steps = stepErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!canWorkflow('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      
      const payload = {
        workflowName: formData.workflowName,
        jobTitleId: steps[0]?.jobTitleId ? parseInt(steps[0].jobTitleId) : null,
        qualificationId: steps[0]?.qualificationId ? parseInt(steps[0].qualificationId) : null,
        description,
        steps: steps.map(step => ({
          step: step.step,
          jobTitleId: step.jobTitleId ? parseInt(step.jobTitleId) : null,
          qualificationId: step.qualificationId ? parseInt(step.qualificationId) : null,
          revisionTargetStep: step.revisionTargetStep ? parseInt(step.revisionTargetStep) : null,
          rejectTargetStep: step.rejectTargetStep ? parseInt(step.rejectTargetStep) : null
        })),
        enableRevision,
        enableReject
      };

      if (editingId) {
        await api.put(`/audit/workflows/${editingId}`, payload);
        setSuccessMessage('Workflow updated successfully');
      } else {
        await api.post(`/audit/workflows`, payload);
        setSuccessMessage('Workflow created successfully');
      }

      setShowSuccessModal(true);
      resetFormData();
      fetchWorkflows(search);
    } catch (error) {
      console.error('Save error:', error);
      setFailMessage(error?.response?.data?.message || 'Failed to save workflow');
      setShowFailModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (workflow) => {
    if (!canWorkflow('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
    if (!canWorkflow('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
    setFormData({
      workflowName: workflow.workflowName || ''
    });
    setDescription(workflow.description || '');
    if (Array.isArray(workflow.steps) && workflow.steps.length > 0) {
      setSteps(workflow.steps.map((step) => ({
        step: step.step,  // Use original step number, not index
        jobTitleId: step.jobTitleId ? String(step.jobTitleId) : '',
        qualificationId: step.qualificationId ? String(step.qualificationId) : '',
        revisionTargetStep: step.revisionTargetStep ? String(step.revisionTargetStep) : (step.step === 1 ? '' : '1'),
        rejectTargetStep: step.rejectTargetStep === 0 || step.rejectTargetStep === '0'
          ? '0'
          : (step.rejectTargetStep ? String(step.rejectTargetStep) : (step.step === 1 ? '' : ''))
      })));
    } else {
      setSteps([{ step: 1, jobTitleId: workflow.jobTitleId ? String(workflow.jobTitleId) : '', qualificationId: workflow.qualificationId ? String(workflow.qualificationId) : '', revisionTargetStep: '', rejectTargetStep: '' }]);
    }
    setEnableRevision(Boolean(workflow.enableRevision ?? true));
    setEnableReject(Boolean(workflow.enableReject ?? false));
    setEditingId(workflow.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = (workflow) => {
    if (!canWorkflow('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
    if (!canWorkflow('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
    setConfirmAction({
      title: 'Delete Workflow',
      message: `Are you sure you want to delete the workflow "${workflow.workflowName}"?`,
      onConfirm: async () => {
        try {
          setIsLoading(true);
          await api.delete(`/audit/workflows/${workflow.id}`);
          setSuccessMessage('Workflow deleted successfully');
          setShowSuccessModal(true);
          setShowDeleteConfirmation(false);
          fetchWorkflows(search);
        } catch (error) {
          console.error('Delete error:', error);
          setFailMessage(error?.response?.data?.message || error?.message || 'Failed to delete workflow');
          setShowFailModal(true);
          setShowDeleteConfirmation(false);
        } finally {
          setIsLoading(false);
        }
      }
    });
    setShowDeleteConfirmation(true);
  };

  // Handle export
  const handleExport = async () => {
    try {
      const response = await api.get(`/audit/workflows/export`, {
        params: { search: searchTerm },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'workflows.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      setFailMessage('Failed to export workflows');
      setShowFailModal(true);
    }
  };



  return (
    <>
        <div className="container-xl">
          <div className="page-header d-print-none">
            <div className="row align-items-center">
              <div className="col">
                <h2 className="page-title">
                  Workflow Approval
                </h2>
                <p className="text-muted">Streamline the auditor approval process for competencies, qualifications, and upgrades.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body">
          <div className="container-xl">
            <div className="row row-deck row-cards">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="row mb-3 align-items-center">
                      <div className="col-auto">
                        <div className="input-icon">
                          <span className="input-icon-addon">
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon" width={24} height={24} viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <circle cx={10} cy={10} r={7} />
                              <line x1={21} y1={21} x2={15} y2={15} />
                            </svg>
                          </span>
                          <input type="text" className="form-control" placeholder="Search ..." value={searchTerm} onChange={(e) => {
                            setSearchTerm(e.target.value);
                            handleSearchChange(e.target.value);
                          }} />
                        </div>
                      </div>
                      <div className="col-auto ms-auto">
                        <button className="btn btn-icon btn-white" onClick={handleExport} title="Export">
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width={24} height={24} viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                            <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                            <path d="M12 17v-6" />
                            <path d="M9.5 14.5l2.5 2.5l2.5 -2.5" />
                          </svg>
                        </button>
                        <button className="btn btn-primary ms-2" onClick={() => {
                          resetFormData();
                          setShowForm(true);
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width={24} height={24} viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <line x1={12} y1={5} x2={12} y2={19} />
                            <line x1={5} y1={12} x2={19} y2={12} />
                          </svg>
                          Create Workflow
                        </button>
                      </div>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-vcenter card-table">
                        <thead>
                          <tr>
                            <th style={{ width: '1%' }}>
                              <input className="form-check-input" type="checkbox" checked={selectedIds.length === workflows.length && workflows.length > 0} onChange={handleSelectAll(workflows)} />
                            </th>
                            <th>Workflow Name</th>
                            <th>Approval Step Setting</th>
                            <th className="w-1" style={{ textAlign: 'center' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan="5" className="text-center text-muted py-4">Loading...</td>
                            </tr>
                          ) : workflows.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center text-muted py-4">No workflows found</td>
                            </tr>
                          ) : (
                            workflows.map((workflow) => (
                              <tr key={workflow.id}>
                                <td>
                                  <input className="form-check-input" type="checkbox" checked={selectedIds.includes(workflow.id)} onChange={(e) => handleCheckboxClick(e, workflow.id)} />
                                </td>
                                <td>
                                  <div className="text-truncate">
                                    <a href="#" className="text-reset" onClick={(e) => {
                                      e.preventDefault();
                                      handleEdit(workflow);
                                    }}>
                                      {workflow.workflowName}
                                    </a>
                                  </div>
                                </td>
                                <td>
                                  {/* Approval Step Setting summary */}
                                  <div style={{ minWidth: 220 }}>
                                    {(workflow.steps || []).map((step, idx) => (
                                      <div key={idx} style={{ marginBottom: 4, fontSize: 13 }}>
                                        <span className="fw-semibold">Step {step.step}:</span>{' '}
                                        {step.jobTitleId && <span className="badge bg-blue text-light me-1">{getJobTitleName(step.jobTitleId)}</span>}
                                        {step.qualificationId && <span className="badge bg-green text-light me-1">{getQualificationName(step.qualificationId)}</span>}
                                        {workflow.enableRevision && step.step > 1 && step.revisionTargetStep && (
                                          <span className="badge bg-yellow text-dark me-1">Revision → Step {step.revisionTargetStep}</span>
                                        )}
                                        {workflow.enableReject && step.step > 1 && step.rejectTargetStep !== null && step.rejectTargetStep !== undefined && step.rejectTargetStep !== '' && (
                                          <span className="badge bg-red text-light me-1">
                                            Reject → {getRejectTargetLabel(step.rejectTargetStep)}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </td>
                                <td>
                                  <div className="btn-list flex-nowrap">
                                    <button className="btn btn-sm btn-icon btn-ghost-primary" onClick={() => handleEdit(workflow)} title="Edit" disabled={isLoading}>
                                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width={24} height={24} viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                                        <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                                        <path d="M16 5l3 3" />
                                      </svg>
                                    </button>
                                    <button className="btn btn-sm btn-icon btn-ghost-danger" onClick={() => handleDelete(workflow)} title="Delete" disabled={isLoading}>
                                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width={24} height={24} viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <line x1={4} y1={7} x2={20} y2={7} />
                                        <line x1={10} y1={11} x2={10} y2={17} />
                                        <line x1={14} y1={11} x2={14} y2={17} />
                                        <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                                        <path d="M9 7v-1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v1" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex align-items-center justify-content-between mt-4">
                      <div>
                        <select
                          className="form-select w-auto"
                          value={pagination.size}
                          onChange={(e) => {
                            pagination.setSize(Number(e.target.value));
                            pagination.setPage(0);
                          }}
                        >
                          <option value="10">10 records</option>
                          <option value="25">25 records</option>
                          <option value="50">50 records</option>
                          <option value="100">100 records</option>
                        </select>
                      </div>
                      <Pagination {...pagination} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <>
          <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title">{editingId ? 'Edit Workflow' : 'Create Workflow'}</h5>
                    <div className="text-muted small">Streamline the auditor approval process for competencies, qualifications, and upgrades.</div>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setShowForm(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Workflow Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${errors.workflowName ? 'is-invalid' : ''}`}
                      value={formData.workflowName}
                      onChange={(e) => handleFormChange('workflowName', e.target.value)}
                      placeholder="Workflow Name"
                    />
                    {errors.workflowName && <div className="invalid-feedback">{errors.workflowName}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                        if (errors.description) {
                          setErrors((prev) => ({ ...prev, description: '' }));
                        }
                      }}
                      placeholder="Brief description of when to use this workflow (optional)"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Approval Step Setting</label>
                    {steps.map((step, index) => (
                      <div className="card mb-3" key={`step-${step.step}`}>
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="card-title m-0">Step {step.step}</h4>
                            {steps.length > 1 && (
                              <button type="button" className="btn btn-sm btn-icon btn-outline-danger" onClick={() => removeStep(index)} title="Remove step">
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="18" height="18" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              </button>
                            )}
                          </div>

                          <div className="row g-3">
                            {errors.steps?.[index]?.general && (
                              <div className="col-12">
                                <div className="alert alert-danger mb-0" role="alert">
                                  {errors.steps[index].general}
                                </div>
                              </div>
                            )}
                            <div className="col-md-6">
                              <TomSelect
                                name={`jobTitle-${index}`}
                                value={step.jobTitleId}
                                onChange={(_, value) => {
                                  handleStepChange(index, 'jobTitleId', value);
                                  // Jika pilih jobTitle, kosongkan qualification
                                  if (value) handleStepChange(index, 'qualificationId', '');
                                }}
                                options={jobTitleOptions}
                                valueField="value"
                                labelField="label"
                                searchField="label"
                                placeholder="Select Job Title"
                                label="Job Title"
                                disabled={!!step.qualificationId}
                              />
                            </div>
                            <div className="col-md-6">
                              <TomSelect
                                name={`qualification-${index}`}
                                value={step.qualificationId}
                                onChange={(_, value) => {
                                  handleStepChange(index, 'qualificationId', value);
                                  // Jika pilih qualification, kosongkan jobTitle
                                  if (value) handleStepChange(index, 'jobTitleId', '');
                                }}
                                options={competencyOptions}
                                valueField="value"
                                labelField="label"
                                searchField="label"
                                placeholder="Select Qualification..."
                                label="Qualification"
                                disabled={!!step.jobTitleId}
                              />
                            </div>
                            <div className="col-12">
                              <small className="text-muted">
                                <span className="fw-semibold">Note:</span> You can only select <b>either Job Title <span className='text-primary'>OR</span> Qualification</b> for each step.
                              </small>
                            </div>

                            {index > 0 && (
                              <>
                                {enableRevision && (
                                  <div className="col-12">
                                    <label className="form-label">IF revision requested, send to:</label>
                                    <div>
                                      <select
                                        className={`form-select ${errors.steps?.[index]?.revisionTargetStep ? 'is-invalid' : ''}`}
                                        value={step.revisionTargetStep}
                                        onChange={(e) => handleStepChange(index, 'revisionTargetStep', e.target.value)}
                                      >
                                        <option value="">Select target</option>
                                        {getRevisionTargetOptions(index).map((opt) => (
                                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                      </select>
                                      {step.revisionTargetStep && (
                                        <small className="text-muted d-block mt-1">
                                          Selected: <span className="fw-semibold">Back to Assignee (Step {step.revisionTargetStep})</span>
                                        </small>
                                      )}
                                      {errors.steps?.[index]?.revisionTargetStep && (
                                        <div className="invalid-feedback d-block">{errors.steps?.[index]?.revisionTargetStep}</div>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {enableReject && (
                                  <div className="col-12">
                                    <label className="form-label">IF rejected, send to:</label>
                                    <div>
                                      <select
                                        className={`form-select ${errors.steps?.[index]?.rejectTargetStep ? 'is-invalid' : ''}`}
                                        value={step.rejectTargetStep}
                                        onChange={(e) => handleStepChange(index, 'rejectTargetStep', e.target.value)}
                                      >
                                        <option value="">Select action</option>
                                        {getRejectTargetOptions(index).map((opt) => (
                                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                      </select>
                                      {step.rejectTargetStep && (
                                        <small className="text-muted d-block mt-1">
                                          Selected: <span className="fw-semibold">{getRejectTargetLabel(step.rejectTargetStep)}</span>
                                        </small>
                                      )}
                                      {errors.steps?.[index]?.rejectTargetStep && (
                                        <div className="invalid-feedback d-block">{errors.steps?.[index]?.rejectTargetStep}</div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    <button type="button" className="btn btn-outline-primary" onClick={addStep}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="18" height="18" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Add Step
                    </button>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Revision Button</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="revisionEnabled"
                          checked={enableRevision}
                          onChange={(e) => {
                            setEnableRevision(e.target.checked);
                            if (!e.target.checked) {
                              // Reset revisionTargetStep for all steps except first
                              setSteps((prev) => prev.map((step, idx) => idx === 0 ? step : { ...step, revisionTargetStep: '' }));
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor="revisionEnabled">
                          Checklist to activate the revision feature on the competency application form
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Reject Button</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="rejectEnabled"
                          checked={enableReject}
                          onChange={(e) => {
                            setEnableReject(e.target.checked);
                            if (!e.target.checked) {
                              // Reset rejectTargetStep for all steps except first
                              setSteps((prev) => prev.map((step, idx) => idx === 0 ? step : { ...step, rejectTargetStep: '' }));
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor="rejectEnabled">
                          Checklist to activate the rejected feature on the competency application form
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save Workflow'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setShowForm(false)}></div>
        </>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal isOpen={showSuccessModal} message={successMessage} onClose={() => {
          setShowSuccessModal(false);
          setSuccessMessage('');
        }} />
      )}

      {/* Fail Modal */}
      {showFailModal && (
        <FailModal isOpen={showFailModal} message={failMessage} onClose={() => {
          setShowFailModal(false);
          setFailMessage('');
        }} />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal 
        isOpen={showDeleteConfirmation && confirmAction}
        title={confirmAction?.title || 'Delete Workflow'}
        message={confirmAction?.message || 'Are you sure?'}
        onConfirm={async () => {
          console.log('Confirmation modal onConfirm triggered');
          if (confirmAction?.onConfirm && typeof confirmAction.onConfirm === 'function') {
            await confirmAction.onConfirm();
          }
        }}
        onClose={() => {
          console.log('Confirmation modal onClose triggered');
          setShowDeleteConfirmation(false);
        }}
        isLoading={isLoading}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </>
  );
};

export default Workflow;
